"use client"

import { useState } from "react"
import { ChevronDown } from "lucide-react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type FooterSection = {
  title: string
  links: Array<{
    label: string
    href: string
    external?: boolean
  }>
}

type FooterMobileNavProps = {
  sections: FooterSection[]
}

/**
 * Collapsible accordion for footer navigation on mobile.
 * Client component because it needs toggle state.
 */
const FooterMobileNav = ({ sections }: FooterMobileNavProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index))
  }

  return (
    <div className="small:hidden divide-y divide-neutral-200">
      {sections.map((section, index) => {
        const isOpen = openIndex === index
        return (
          <div key={section.title}>
            <button
              onClick={() => toggle(index)}
              className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-brand-navy"
              aria-expanded={isOpen}
            >
              <span>{section.title}</span>
              <ChevronDown
                className={`w-4 h-4 text-grey-40 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isOpen && (
              <ul className="px-4 pb-3 space-y-2">
                {section.links.map((link) =>
                  link.external ? (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-brand-navy hover:text-brand-primary transition-colors"
                      >
                        {link.label}
                      </a>
                    </li>
                  ) : (
                    <li key={link.label}>
                      <LocalizedClientLink
                        href={link.href}
                        className="text-sm text-brand-navy hover:text-brand-primary transition-colors"
                      >
                        {link.label}
                      </LocalizedClientLink>
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default FooterMobileNav
